<template lang="pug">
div.summary
    div.summary-content.container-loading.is-loading(v-if="isLoading")
        div.content
            div.stripe.long-stripe.category
            div.stripe.medium-long-stripe.category
            div.stripe.long-stripe.category
            div.stripe.medium-long-stripe.category
            div.stripe.long-stripe.category
            div.stripe.medium-long-stripe.category
            div.stripe.long-stripe.category
            div.stripe.medium-long-stripe.category
            div.stripe.long-stripe.category
            div.stripe.medium-long-stripe.category
            div.stripe.small-stripe.category

    div.summary-content(v-else)
        p.no-summary(v-if="reformattedSummary === ''") There is no summary available.
        template(v-else v-for="(box, boxIndex) in reformattedSummary")
            //- OPERATORS
            div.operator.operator-add(v-if="boxIndex !== 0 && isInclusionBox(box)")
                svg-icon(name='add' size="small")
            div.operator.operator-minus(v-else-if="boxIndex !== 0 && !isInclusionBox(box)")
                svg-icon(name='minus' size="small")
            //- FORMULA SCOPE
            div.summary-formula-scope(
                v-if="boxIndex === 0"
                :class="{'open': displayScope}")
                button.btn-formula-scope(@click="toggleDisplayScope") {{ formulaScopeText }} Formula Scope
                div.summary-box(v-if="displayScope")
                    svg-icon.cursor(
                        name='close' size="small"
                        @click.native="toggleDisplayScope"
                        title="Hide formula scope")
                    p.summary-criterion(v-for="line in box")
                        template(v-for="(chunk, idx) in line.split('*')")
                            span(v-if="idx % 2 == 0 && isPrintableLine(chunk)") {{ chunk }}
                            span.summary-highlight(v-else-if="idx!==0") {{ chunk }}
            //- SUMMARY
            div.summary-box(v-else)
                p.summary-criterion(v-for="line in box")
                    template(v-for="(chunk, idx) in line.split('*')")
                        span(v-if="idx % 2 == 0 && isPrintableLine(chunk)") {{ chunk }}
                        span.summary-highlight(v-else-if="idx!==0") {{ chunk }}
</template>

<script>
import { mapState } from 'vuex'
import { cloneDeep } from 'lodash'

export default {
    name: 'Summary',

    props: {
        // eslint-disable-next-line
        summary: {
            type: [Object, Array],
            required: false
        },
        loading: {
            type: [Boolean, Number],
            default: false
        },
        parent: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            displayScope: false
        }
    },

    watch: {
        isLoading: {
            handler: function(newVal) {
                if (!newVal) {
                    this.displayScope = this.summary?.length === 1
                }
            }
        }
    },

    computed: {
        ...mapState('ui/monitoring', {
            audienceIsLoading: state => state.isLoading.summary
        }),

        isLoading() {
            return this.loading || this.audienceIsLoading
        },

        formulaScopeText() {
            return this.displayScope ? 'Hide' : 'Show'
        },

        reformattedSummary() {
            const individualBox = []
            if (this.summary) {
                // Case where several boxes 'AND' and a 'NOT' box, with one of the and
                if (this.summary.length === 2 && this.summary[1][0] === 'AND') {
                    this.summary[1].forEach(element => {
                        if (element !== 'AND') individualBox.push(['OR', element])
                    })
                    return [this.summary[0], ...individualBox]
                }

                // Case where atleast 3 boxes 'AND' the first one having multiple criterion the others having only one criterion
                else if (this.summary?.[1]?.[0] === 'AND' && this.summary?.[2]?.[0] === 'OR') {
                    if (this.summary?.[1].length === 3 && this.summary.length < 3) {
                        this.summary?.[2].forEach(element => {
                            if (element !== 'OR') individualBox.push(['OR', element])
                        })
                        return [this.summary?.[0], this.summary?.[1], ...individualBox, this.summary?.[3]]
                    } else if (this.summary?.[1].length === 3 && this.summary.length >= 3) {
                        // Case where there are 3 elements in a box 'AND' with also several elements in other 'And' boxes and some having only criterion
                        this.summary[1].forEach(element => {
                            if (element !== 'AND') individualBox.push(['OR', element])
                        })
                        const clone = cloneDeep(this.summary)
                        clone.splice(0, 2)
                        return [this.summary[0], ...individualBox, ...clone]
                    } else {
                        // same case but with more than 2 other 'AND', with also 'NOR' boxes possible
                        this.summary[1].forEach(element => {
                            if (element !== 'AND') individualBox.push(['OR', element])
                        })
                        const clone = cloneDeep(this.summary)
                        clone.splice(0, 2)
                        return [this.summary[0], ...individualBox, ...clone]
                    }
                }

                // Case where several boxes 'AND', with ONLY 1 element per box, with a 'NOT' box
                else if (this.summary?.[0]?.[0] === 'OR' && this.summary?.[1]?.[0] !== 'OR' && this.summary?.[2]?.[0] === 'NOR') {
                    this.summary[1].forEach(element => {
                        if (element !== 'AND' && element !== 'OR' && element !== '') individualBox.push(['OR', element])
                    })
                    return [this.summary[0], ...individualBox, this.summary.at(-1)]
                }
            }
            return this.summary
        }
    },

    methods: {
        isPrintableLine(line) {
            if (line === 'OR' || line === 'NOR' || line === 'AND') return false
            return true
        },

        isInclusionBox(box) {
            if (!box) return

            //Box with a length of 1 means it doesn't start with 'NOR' therefore it is an inclusion box
            if (box[0] === 'OR' || box[0] === 'AND' || box.length === 1) return true
            return false
        },

        toggleDisplayScope() {
            this.displayScope = !this.displayScope
        }
    }
}
</script>
